/**
 * Step 1: Select Assets
 * Choose 2+ Smart Assets to compose together.
 */
import type React from "react";
import type { SmartAssetData } from "../types";

export interface SelectAssetsStepProps {
  availableAssets: SmartAssetData[];
  selectedAssetIds: string[];
  toggleAsset: (id: string) => void;
  isAssetSelected: (id: string) => boolean;
}

export const SelectAssetsStep: React.FC<SelectAssetsStepProps> = ({
  availableAssets,
  selectedAssetIds,
  toggleAsset,
  isAssetSelected,
}) => (
  <div>
    <div className="mb-4">
      <p className="text-sm text-sa-text-muted">
        Select 2 or more Smart Assets to compose together.
        <span className="ml-2 font-medium text-sa-success">{selectedAssetIds.length} selected</span>
      </p>
    </div>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {availableAssets.map((asset) => (
        <button
          key={asset.id}
          onClick={() => toggleAsset(asset.id)}
          className={`p-4 rounded-lg border-2 text-left transition-all ${
            isAssetSelected(asset.id)
              ? "border-sa-success bg-sa-success/10"
              : "border-sa-border hover:border-sa-border-hover"
          }`}
          data-testid={`select-asset-${asset.id}`}
        >
          <div className="flex items-center gap-3">
            {asset.iconPath && (
              <img src={asset.iconPath} alt={asset.name} className="w-10 h-10 rounded-lg" />
            )}
            <div>
              <p className="font-medium text-sa-text">{asset.name}</p>
              <p className="text-xs text-sa-text-muted">{asset.ticker}</p>
            </div>
            {isAssetSelected(asset.id) && (
              <svg
                className="w-5 h-5 text-sa-success ml-auto"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          <p className="mt-2 text-xs text-sa-text-muted line-clamp-2">{asset.description}</p>
        </button>
      ))}
    </div>
  </div>
);
